/*
 * Product card interactions — scoped per [data-product-card] root.
 * Hooks: [data-card-gallery], [data-card-image], [data-card-prev/next],
 * [data-card-gallery-current], [data-quick-add-trigger/drawer/size],
 * [data-swatch][data-swatch-preview][data-swatch-url][data-swatch-variants],
 * [data-wishlist]. Drawer dismissal is via Escape, mouseleave, and
 * tap-outside (S6) — there is no X button.
 * Helpers (setSwatchActive, applyVariantMap, showSlide, showFade, makeDrawer)
 * live in assets/product-card-variant.js under window.BroraProductCardVariant
 * so each file stays under the 150-line JS cap.
 */
const READY = 'data-product-card-ready';
const SWIPE = 40;
const LG = window.matchMedia('(min-width: 1024px)');
const V = (typeof window !== 'undefined' && window.BroraProductCardVariant) || {};

// Pre-order can only be completed on the PDP: a quick-add of a pre-order variant must NOT add to
// bag — it routes the shopper to the PDP with the variant selected. Routing is per variant
// (data-preorder on the size button, kept in sync by applyVariantMap); the card-level
// data-product-preorder check is an inert guard, emitted by no markup. Returns true when it routed
// (caller must not add).
function preorderRoute(card, trigger) {
  const isPreorder = card.hasAttribute('data-product-preorder') || (trigger && trigger.hasAttribute('data-preorder'));
  if (!isPreorder) return false;
  const url = card.getAttribute('data-product-url');
  const id = trigger && trigger.dataset.variantId;
  if (url && id) window.location.href = url + (url.indexOf('?') >= 0 ? '&' : '?') + 'variant=' + id;
  return true;
}

function initCard(card) {
  if (card.hasAttribute(READY)) return;
  card.setAttribute(READY, '');

  const gallery = card.querySelector('[data-card-gallery]');
  const galleryScroll = (gallery && gallery.querySelector('[data-card-gallery-scroll]')) || gallery;
  const slides = galleryScroll ? Array.from(galleryScroll.querySelectorAll('[data-card-image]')) : [];
  const counter = card.querySelector('[data-card-gallery-current]');
  const counterWrap = card.querySelector('[data-card-gallery-counter]');
  const trigger = card.querySelector('[data-quick-add-trigger]');
  const drawer = card.querySelector('[data-quick-add-drawer]');
  const sizeBtns = Array.from(card.querySelectorAll('[data-quick-add-size]'));
  const swatches = Array.from(card.querySelectorAll('[data-swatch]'));
  const wishlist = card.querySelector('[data-wishlist]');
  const galleryEnabled = card.hasAttribute('data-gallery-enabled') && slides.length > 1;
  let current = 0;

  if (LG.matches && slides.length > 1) {
    slides.forEach((s, i) => {
      s.classList.add(i === 0 ? 'translate-x-0' : 'translate-x-full');
      s.classList.add(i === 0 ? 'opacity-100' : 'opacity-0');
    });
  }

  // Slide 0 is always needed. On desktop slide 1 is too: it is the mouseenter crossfade target, and a
  // fade into an unhydrated image reads as a broken card. Everything past that waits for a real
  // navigation. Below lg the scroller has real geometry, so an observer does the deferral properly.
  // galleryEnabled gates the slide-1 warm: on a card with the gallery disabled the hover preview is
  // never bound, so pre-fetching slide 1 there is one wasted image per card (P2-5).
  V.hydrateSlide?.(slides[0]);
  if (LG.matches) {
    if (galleryEnabled && slides.length > 1) V.hydrateSlide?.(slides[1]);
  } else {
    V.observeHydration?.(galleryScroll, slides);
  }

  function show(i, { mode = 'slide', dir } = {}) {
    if (!slides.length || i < 0 || i >= slides.length || i === current) return;
    // Promote the incoming slide before the transition starts, then warm the wrapped neighbours so the
    // next arrow press is instant too.
    V.hydrateSlide?.(slides[i]);
    if (LG.matches && slides.length > 2) {
      V.hydrateSlide?.(slides[(i + 1) % slides.length]);
      V.hydrateSlide?.(slides[(i - 1 + slides.length) % slides.length]);
    }
    if (!LG.matches) {
      current = i;
      if (counter) counter.textContent = String(i + 1);
      galleryScroll.scrollTo({ left: slides[i].offsetLeft, behavior: mode === 'fade' ? 'auto' : 'smooth' });
      return;
    }
    const outgoing = slides[current];
    const incoming = slides[i];
    // Loop wraps the index, so honour the caller's direction when given; fall back
    // to positional direction (hover previews pass no dir).
    const forward = dir === undefined ? i > current : dir;
    if (mode === 'slide') {
      V.showSlide(outgoing, incoming, forward);
    } else {
      V.showFade(outgoing, incoming);
    }
    outgoing.setAttribute('aria-hidden', 'true'); outgoing.toggleAttribute('inert', true);
    incoming.removeAttribute('aria-hidden'); incoming.removeAttribute('inert');
    current = i;
    if (counter) counter.textContent = String(i + 1);
  }

  if (galleryEnabled) {
    // Gallery loops: prev on the first image wraps to the last, next on the last
    // wraps to the first. Direction is passed explicitly so the slide animates the
    // intuitive way across the wrap (reuses the transform transitions — no clones).
    const prev = () => show((current - 1 + slides.length) % slides.length, { mode: 'slide', dir: false });
    const next = () => show((current + 1) % slides.length, { mode: 'slide', dir: true });
    card.querySelector('[data-card-prev]')?.addEventListener('click', (e) => { e.preventDefault(); prev(); });
    card.querySelector('[data-card-next]')?.addEventListener('click', (e) => { e.preventDefault(); next(); });
    if (!card.hasAttribute('data-gallery-no-touch') && LG.matches) {
      let touchX = null;
      galleryScroll.addEventListener('touchstart', (e) => { touchX = e.touches[0].clientX; }, { passive: true });
      galleryScroll.addEventListener('touchend', (e) => {
        if (touchX === null) return;
        const d = e.changedTouches[0].clientX - touchX;
        if (Math.abs(d) > SWIPE) { if (d < 0) next(); else prev(); }
        touchX = null;
      });
    }
    card.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    });
    if (LG.matches) {
      // Preview the 2nd own image on card hover; back to the first on leave.
      card.addEventListener('mouseenter', () => show(1, { mode: 'fade' }));
      card.addEventListener('mouseleave', () => show(0, { mode: 'fade' }));
    } else {
      V.observeScroll(galleryScroll, slides, (idx) => {
        current = idx;
        if (counter) counter.textContent = String(idx + 1);
      });
    }
  }

  V.bindSwatches(card, swatches, counterWrap);

  // A direct-add trigger (default-variant products, in stock) adds the single
  // variant straight to bag — no size drawer. Otherwise the trigger opens the
  // drawer. Guard makeDrawer: direct-add cards have no drawer element.
  let closeDrawer = function () {};
  if (trigger && trigger.hasAttribute('data-quick-add-direct')) {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (preorderRoute(card, trigger)) return; // pre-order → PDP, never add
      trigger.dispatchEvent(new CustomEvent('product-card:add', {
        bubbles: true,
        detail: { variantId: trigger.dataset.variantId, card },
      }));
    });
  } else if (trigger && drawer) {
    const d = V.makeDrawer(card, drawer, trigger, sizeBtns);
    closeDrawer = d.close;
    trigger.addEventListener('click', (e) => { e.preventDefault(); d.open({ viaKeyboard: e.detail === 0 }); });
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && card.hasAttribute('data-quick-add-open')) { e.preventDefault(); d.close(); }
    });
    card.addEventListener('mouseleave', () => {
      if (card.hasAttribute('data-quick-add-open')) d.close({ returnFocus: false });
    });
  }

  sizeBtns.forEach((btn) => {
    if (btn.hasAttribute('aria-disabled')) return;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (preorderRoute(card, btn)) return; // pre-order → PDP, never add
      btn.dispatchEvent(new CustomEvent('product-card:add', {
        bubbles: true,
        detail: { variantId: btn.dataset.variantId, card },
      }));
      closeDrawer();
    });
  });

  if (wishlist && window.BroraProductCardWishlist?.bind) {
    window.BroraProductCardWishlist.bind(wishlist);
  }
}

function initAll(root = document) {
  root.querySelectorAll('[data-product-card]').forEach(initCard);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}
document.addEventListener('shopify:section:load', (e) => initAll(e.target));
